import { Driver, DriverHook } from "driver.js";
import { defaultTourConfig } from ".";

// Export the tour steps so they can be reused in GrandTour
export const profileSetupTourSteps = [
  {
    element: "#settings",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Welcome to Affiliate Profile Setup!</span>
        </div>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Let's get your affiliate program set up for success! This quick tour will guide you through completing your Affiliate profile.</p>
        <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs text-brand-800 font-medium">Complete all sections to maximize your affiliate revenue!</p>
          </div>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Estimated time: 2 minutes</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#personal",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Personal Profile Setup</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Let's start with your personal information. This section provides relevant information about you.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Make sure to fill in the following fields:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                </svg>
                Full name and email address
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Your current location (country)
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>This information is used to verify your identity.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#profile",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Profile Information</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Now let's set up your profile details. This information will be visible to potential brands.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
           <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Profile Details to Add:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                Your username and bio
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                Profile picture (recommended)
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                Select your niches
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                  </svg>
                  Add your social media links
                </div>
                <div class="grid grid-cols-5 gap-2 pl-4 pt-1">
                  <img src="https://cdn.simpleicons.org/facebook/7F56D9" class="w-4 h-4" alt="Facebook" title="Facebook"/>
                  <img src="https://cdn.simpleicons.org/instagram/7F56D9" class="w-4 h-4" alt="Instagram" title="Instagram"/>
                  <img src="https://cdn.simpleicons.org/tiktok/7F56D9" class="w-4 h-4" alt="TikTok" title="TikTok"/>
                  <img src="https://cdn.simpleicons.org/youtube/7F56D9" class="w-4 h-4" alt="YouTube" title="YouTube"/>
                  <img src="https://cdn.simpleicons.org/discord/7F56D9" class="w-4 h-4" alt="Discord" title="Discord"/>
                </div>
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>This is an opportunity to showcase your skills and attract quality brands.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#payment",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Payment Information</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">This is where brands send your earnings. You can skip it for now — we'll ask when you request your first payout.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">One method is enough. Brands pay through a method they support, so adding a couple gives you more options.</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                The following payment methods are available:
                <div class="mt-2 grid grid-cols-5 gap-2" style="max-width: 120px">
                  <img src="https://static.cdnlogo.com/logos/p/42/paypal-icon.svg" alt="PayPal" class="w-4 h-4"/>
                  <img src="https://static.cdnlogo.com/logos/b/46/bitcoin.svg" alt="Bitcoin" class="w-4 h-4"/>
                  <img src="https://static.cdnlogo.com/logos/z/89/zelle.svg" alt="Zelle" class="w-4 h-4"/>
                  <img src="https://static.cdnlogo.com/logos/c/53/cash-app.svg" alt="Cash App" class="w-4 h-4"/>
                  <img src="https://static.cdnlogo.com/logos/v/20/venmo.svg" alt="Venmo" class="w-12 h-4"/>
                </div>
                </div>
                </div>
                </ol>
                </div>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Multiple payment options helps you receive your earnings easily.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
];

const startProfileTour = (
  tourDriver: Driver,
  onNextClick: DriverHook,
  onPrevClick: DriverHook
) => {
  tourDriver.setConfig(defaultTourConfig);
  // Here we set the unique steps for the FIRST tour
  tourDriver.setSteps(
    profileSetupTourSteps.map((step) => ({
      ...step,
      popover: {
        ...step.popover,
        onNextClick: (element, step, config) => {
          onNextClick(element, step, config);
          setTimeout(() => {
            tourDriver.moveNext();
          }, 200);
        },
        onPrevClick: (element, step, config) => {
          onPrevClick(element, step, config);
          setTimeout(() => {
            tourDriver.movePrevious();
          }, 200);
        },
      },
    }))
  );
  tourDriver.drive();
};

export default startProfileTour;
